import { DatePicker, Input, Select } from 'antd'
import { Dayjs } from 'dayjs'
import { DrilldownFieldType } from 'features/drilldown/rolodex/ontology/ontologyTypes'
import { FC, useMemo } from 'react'

import {
  ErrorText,
  formatValueForDisplay,
  getDefaultPlaceholder,
  parseAndValidateDate,
  useOntologyEnumSelectOptions,
} from './utils'
import { getValidationStatus, validateFieldValue } from './validation'

const { TextArea } = Input

export interface PropertyValueEditorProps {
  type: DrilldownFieldType
  value: unknown
  onChange: (value: string) => void
  size?: 'small' | 'middle' | 'large'
  rows?: number
  style?: React.CSSProperties
  placeholder?: string
  fieldKey?: string
  onValidationChange?: (isValid: boolean) => void
}

export const PropertyValueEditor: FC<PropertyValueEditorProps> = ({
  type,
  value,
  onChange,
  fieldKey,
  size = 'middle',
  rows = 1,
  style,
  placeholder,
}) => {
  const enumOptions = useOntologyEnumSelectOptions(type, fieldKey || '')

  const displayValue = useMemo(() => {
    return formatValueForDisplay(value, type)
  }, [value, type])

  const validationError = useMemo(() => {
    const validation = validateFieldValue(displayValue, type)
    return validation.error
  }, [displayValue, type])

  const validationStatus = useMemo(() => {
    return getValidationStatus(displayValue, type)
  }, [displayValue, type])

  const defaultPlaceholder = placeholder || getDefaultPlaceholder(type)

  switch (type) {
    case 'text':
      return (
        <TextArea
          value={displayValue}
          onChange={(e) => onChange(e.target.value)}
          placeholder={defaultPlaceholder}
          rows={rows}
          size={size}
          style={style}
        />
      )
    case 'number':
      return (
        <Input
          type='number'
          value={displayValue}
          onChange={(e) => onChange(e.target.value)}
          placeholder={defaultPlaceholder}
          size={size}
          style={style}
          status={validationStatus}
        />
      )
    case 'boolean':
      return (
        <Select
          value={displayValue}
          onChange={(val) => onChange(val as string)}
          options={[
            { value: 'true', label: 'True' },
            { value: 'false', label: 'False' },
          ]}
          placeholder={defaultPlaceholder}
          size={size}
          style={style}
          status={validationStatus}
          getPopupContainer={(triggerNode) =>
            triggerNode.parentNode as HTMLElement
          }
        />
      )
    case 'date':
      return (
        <DatePicker
          value={parseAndValidateDate(displayValue)}
          onChange={(date: Dayjs, dateString: string | string[]) => {
            const finalDateString = Array.isArray(dateString)
              ? dateString[0] || ''
              : dateString
            onChange(finalDateString)
          }}
          placeholder={defaultPlaceholder}
          size={size}
          style={style}
          showTime={false}
          format='YYYY-MM-DD'
          status={validationStatus}
        />
      )
    case 'object':
    case 'array':
      return (
        <div>
          <TextArea
            value={displayValue}
            onChange={(e) => onChange(e.target.value)}
            placeholder={defaultPlaceholder}
            rows={3}
            size={size}
            style={{
              ...style,
              fontFamily: 'monospace',
              borderColor: validationError ? '#ff4d4f' : undefined,
            }}
            status={validationStatus}
          />
          {validationError && <ErrorText>{validationError}</ErrorText>}
        </div>
      )
    case 'enum':
      return (
        <Select
          value={displayValue}
          onChange={(val) => onChange(val as string)}
          options={enumOptions}
          placeholder={defaultPlaceholder}
          size={size}
          style={{ ...style, minWidth: '100px' }}
          showSearch
          allowClear
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          getPopupContainer={(triggerNode) =>
            triggerNode.parentNode as HTMLElement
          }
        />
      )
    default:
      return (
        <Input
          value={displayValue}
          onChange={(e) => onChange(e.target.value)}
          placeholder={defaultPlaceholder}
          size={size}
          style={style}
        />
      )
  }
}
